/**
 * @license
 * Copyright 2017 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import * as tf from '@tensorflow/tfjs-core';
import { backend_util, DataStorage, engine, env, kernel_impls, KernelBackend, max, slice_util, TensorBuffer, upcastType, util } from '@tensorflow/tfjs-core';
const nonMaxSuppressionV3Impl = kernel_impls.nonMaxSuppressionV3Impl;
const split = kernel_impls.split;
const tile = kernel_impls.tile;
const topkImpl = kernel_impls.topkImpl;
const whereImpl = kernel_impls.whereImpl;
import * as seedrandom from 'seedrandom';
import { assertNotComplex } from './cpu_util';
export class MathBackendCPU extends KernelBackend {
    constructor() {
        super();
        this.blockSize = 48;
        this.firstUse = true;
        this.data = new DataStorage(this, engine());
    }
    write(values, shape, dtype) {
        if (this.firstUse) {
            this.firstUse = false;
            if (env().get('IS_NODE')) {
                backend_util.warn('\n============================\n' +
                    'Hi there 👋. Looks like you are running TensorFlow.js in ' +
                    'Node.js. To speed things up dramatically, install our node ' +
                    'backend, which binds to TensorFlow C++, by running ' +
                    'npm i @tensorflow/tfjs-node, ' +
                    'or npm i @tensorflow/tfjs-node-gpu if you have CUDA. ' +
                    'Then call require(\'@tensorflow/tfjs-node\'); (-gpu ' +
                    'suffix for CUDA) at the start of your program. ' +
                    'Visit https://github.com/tensorflow/tfjs-node for more details.' +
                    '\n============================');
            }
        }
        const dataId = {};
        this.data.set(dataId, { values, dtype, refCount: 1 });
        return dataId;
    }
    /**
     * Create a data bucket in cpu backend.
     * @param shape Shape of the `TensorInfo`.
     * @param dtype DType of the `TensorInfo`.
     * @param values The value of the `TensorInfo` stored as a flattened array.
     */
    makeTensorInfo(shape, dtype, values) {
        let outId;
        if (dtype === 'string' && values != null && values.length > 0 &&
            util.isString(values[0])) {
            const encodedValues = values.map(d => util.encodeString(d));
            outId = this.write(encodedValues, shape, dtype);
        }
        else {
            outId = this.write(values, shape, dtype);
        }
        return { dataId: outId, shape, dtype };
    }
    /** Increase refCount of a `TensorData`. */
    incRef(dataId) {
        const tensorData = this.data.get(dataId);
        tensorData.refCount++;
    }
    /** Decrease refCount of a `TensorData`. */
    decRef(dataId) {
        if (this.data.has(dataId)) {
            const tensorData = this.data.get(dataId);
            tensorData.refCount--;
        }
    }
    move(dataId, values, shape, dtype) {
        this.data.set(dataId, { values, dtype, refCount: 1 });
    }
    numDataIds() {
        return this.data.numDataIds();
    }
    async read(dataId) {
        return this.readSync(dataId);
    }
    readSync(dataId) {
        const { dtype, complexTensorInfos } = this.data.get(dataId);
        if (dtype === 'complex64') {
            const realValues = this.readSync(complexTensorInfos.real.dataId);
            const imagValues = this.readSync(complexTensorInfos.imag.dataId);
            return backend_util.mergeRealAndImagArrays(realValues, imagValues);
        }
        return this.data.get(dataId).values;
    }
    bufferSync(t) {
        const data = this.readSync(t.dataId);
        let decodedData = data;
        if (t.dtype === 'string') {
            try {
                // Decode the bytes into string.
                decodedData = data.map(d => util.decodeString(d));
            }
            catch (_a) {
                throw new Error('Failed to decode encoded string bytes into utf-8');
            }
        }
        return tf.buffer(t.shape, t.dtype, decodedData);
    }
    makeOutput(values, shape, dtype) {
        const dataId = this.write(values, shape, dtype);
        return engine().makeTensorFromDataId(dataId, shape, dtype, this);
    }
    disposeData(dataId) {
        if (this.data.has(dataId)) {
            const { complexTensorInfos } = this.data.get(dataId);
            if (complexTensorInfos != null) {
                this.disposeData(complexTensorInfos.real.dataId);
                this.disposeData(complexTensorInfos.imag.dataId);
            }
            this.data.delete(dataId);
        }
    }
    disposeIntermediateTensorInfo(tensorInfo) {
        const dataId = tensorInfo.dataId;
        if (this.data.has(dataId)) {
            const tensorData = this.data.get(dataId);
            tensorData.refCount--;
            if (tensorData.refCount < 1) {
                this.disposeData(dataId);
            }
        }
    }
    async time(f) {
        const start = util.now();
        f();
        const kernelMs = util.now() - start;
        return { kernelMs };
    }
    memory() {
        return {
            // Unreliable due to automatic gc. The numbers above are cumulative.
            unreliable: true,
            reasons: ['The reported memory is an upper bound. Due to automatic garbage ' +
                    'collection, the true allocated memory may be less.']
        };
    }
    stridedSlice(x, begin, end, strides) {
        assertNotComplex(x, 'stridedSlice');
        const outShape = slice_util.computeOutShape(begin, end, strides);
        if (outShape.some(axis => axis === 0)) {
            return tf.tensor([], outShape);
        }
        const buffer = tf.buffer(outShape, x.dtype);
        const xBuf = this.bufferSync(x);
        for (let i = 0; i < buffer.size; i++) {
            const loc = buffer.indexToLoc(i);
            const newLoc = new Array(loc.length);
            for (let j = 0; j < newLoc.length; j++) {
                newLoc[j] = loc[j] * strides[j] + begin[j];
            }
            buffer.set(xBuf.get(...newLoc), ...loc);
        }
        return buffer.toTensor();
    }
    diag(x) {
        const xVals = this.readSync(x.dataId);
        const buffer = tf.buffer([x.size, x.size], x.dtype);
        const vals = buffer.values;
        for (let i = 0; i < xVals.length; i++) {
            vals[i * x.size + i] = xVals[i];
        }
        return buffer.toTensor();
    }
    unstack(x, axis) {
        const num = x.shape[axis];
        const outShape = new Array(x.rank - 1);
        let outIndex = 0;
        for (let i = 0; i < x.rank; i++) {
            if (i !== axis) {
                outShape[outIndex++] = x.shape[i];
            }
        }
        const begin = new Array(x.rank).fill(0);
        const size = x.shape.slice();
        size[axis] = 1;
        const res = new Array(num);
        for (let i = 0; i < res.length; i++) {
            begin[axis] = i;
            res[i] = tf.slice(x, begin, size).reshape(outShape);
        }
        return res;
    }
    reverse(x, axis) {
        assertNotComplex(x, 'reverse');
        const buffer = tf.buffer(x.shape, x.dtype);
        const xBuf = this.bufferSync(x);
        for (let i = 0; i < buffer.size; i++) {
            const outLoc = buffer.indexToLoc(i);
            const inLoc = outLoc.slice();
            axis.forEach(ax => inLoc[ax] = x.shape[ax] - 1 - inLoc[ax]);
            buffer.set(xBuf.get(...inLoc), ...outLoc);
        }
        return buffer.toTensor();
    }
    neg(x) {
        assertNotComplex(x, 'neg');
        // TODO(lina128): Use mul directly once neg is modularized.
        return tf.mul(tf.scalar(-1), x);
    }
    addN(tensors) {
        assertNotComplex(tensors, 'addN');
        const vals = tensors.map(t => this.readSync(t.dataId));
        const result = tf.buffer(tensors[0].shape, tensors[0].dtype);
        const resultVals = result.values;
        for (let i = 0; i < tensors.length; i++) {
            const currVals = vals[i];
            for (let j = 0; j < resultVals.length; j++) {
                resultVals[j] += currVals[j];
            }
        }
        return result.toTensor();
    }
    softmax(logits, dim) {
        const axes = util.parseAxisParam([dim], logits.shape);
        // TODO(annxingyuan): Call maxImpl rather than op as part of softmax kernel
        // modularization.
        const maxLogit = max(logits, axes);
        const expandedShape = backend_util.expandShapeToKeepDim(maxLogit.shape, axes);
        // TODO(lina128): Use sub directly once softmax is modularized.
        const a = tf.sub(logits, maxLogit.reshape(expandedShape));
        const b = tf.exp(a);
        const sumExp = this.sum(b, axes).reshape(expandedShape);
        // TODO(annxingyuan): Call divImpl rather than op as part of softmax
        // kernel modularization.
        return tf.div(b, sumExp);
    }
    pow(a, b) {
        assertNotComplex([a, b], 'pow');
        return this.broadcastedBinaryOp(a, b, a.dtype, (aValue, bValue) => Math.pow(aValue, bValue));
    }
    floorDiv(a, b) {
        assertNotComplex([a, b], 'floorDiv');
        const op = (a, b) => Math.floor(a / b);
        const outputDtype = 'int32';
        return this.broadcastedBinaryOp(a, b, outputDtype, op);
    }
    sum(x, axes) {
        assertNotComplex(x, 'sum');
        backend_util.assertAxesAreInnerMostDims('sum', axes, x.rank);
        const [outShape, reduceShape] = backend_util.computeOutAndReduceShapes(x.shape, axes);
        const resultDtype = upcastType(x.dtype, 'int32');
        const result = tf.zeros(outShape, resultDtype);
        const reduceSize = util.sizeFromShape(reduceShape);
        const vals = this.readSync(result.dataId);
        const aVals = this.readSync(x.dataId);
        for (let i = 0; i < vals.length; ++i) {
            const offset = i * reduceSize;
            let sum = 0;
            for (let j = 0; j < reduceSize; ++j) {
                sum += aVals[offset + j];
            }
            vals[i] = sum;
        }
        return result;
    }
    prod(x, axes) {
        assertNotComplex(x, 'sum');
        const [outShape, reduceShape] = backend_util.computeOutAndReduceShapes(x.shape, axes);
        const resultDtype = upcastType(x.dtype, 'int32');
        const result = tf.zeros(outShape, resultDtype);
        const reduceSize = util.sizeFromShape(reduceShape);
        const vals = this.readSync(result.dataId);
        const aVals = this.readSync(x.dataId);
        for (let i = 0; i < vals.length; ++i) {
            const offset = i * reduceSize;
            let prod = 1;
            for (let j = 0; j < reduceSize; ++j) {
                prod *= aVals[offset + j];
            }
            vals[i] = prod;
        }
        return result;
    }
    unsortedSegmentSum(x, segmentIds, numSegments) {
        assertNotComplex(x, 'unsortedSegmentSum');
        const res = [];
        // Reshape the segment id's so that they can be broadcast with
        // x. The new shape should be [segmentIds.shape, 1, ..., 1]
        const numIters = x.rank - segmentIds.rank;
        for (let i = 0; i < numIters; ++i) {
            segmentIds = segmentIds.expandDims(i + 1);
        }
        for (let i = 0; i < numSegments; ++i) {
            const segmentId = tf.scalar(i, 'int32');
            const mask = tf.equal(segmentId, segmentIds).asType('float32');
            const sum = mask.mul(x).sum(0);
            res.push(sum);
        }
        return tf.stack(res);
    }
    argMin(x, axis) {
        assertNotComplex(x, 'argMin');
        const axes = [axis];
        backend_util.assertAxesAreInnerMostDims('argMin', axes, x.rank);
        const [outShape, reduceShape] = backend_util.computeOutAndReduceShapes(x.shape, axes);
        const result = tf.zeros(outShape, 'int32');
        const reduceSize = util.sizeFromShape(reduceShape);
        const vals = this.readSync(result.dataId);
        const aVals = this.readSync(x.dataId);
        for (let i = 0; i < vals.length; ++i) {
            const offset = i * reduceSize;
            let min = aVals[offset];
            let minIndex = 0;
            for (let j = 0; j < reduceSize; ++j) {
                const value = aVals[offset + j];
                if (value < min) {
                    min = value;
                    minIndex = j;
                }
            }
            vals[i] = minIndex;
        }
        return result;
    }
    argMax(x, axis) {
        assertNotComplex(x, 'argMax');
        const axes = [axis];
        backend_util.assertAxesAreInnerMostDims('argMax', axes, x.rank);
        const [outShape, reduceShape] = backend_util.computeOutAndReduceShapes(x.shape, axes);
        const result = tf.zeros(outShape, 'int32');
        const reduceSize = util.sizeFromShape(reduceShape);
        const vals = this.readSync(result.dataId);
        const aVals = this.readSync(x.dataId);
        for (let i = 0; i < vals.length; ++i) {
            const offset = i * reduceSize;
            let max = aVals[offset];
            let maxIndex = 0;
            for (let j = 0; j < reduceSize; ++j) {
                const value = aVals[offset + j];
                if (value > max) {
                    max = value;
                    maxIndex = j;
                }
            }
            vals[i] = maxIndex;
        }
        return result;
    }
    cumsum(x, axis, exclusive, reverse) {
        assertNotComplex(x, 'cumsum');
        if (axis !== x.rank - 1) {
            throw new Error(`backend.cumsum in CPU expects an inner-most axis=${x.rank - 1} ` +
                `but got axis=${axis}`);
        }
        const resultDtype = upcastType(x.dtype, 'int32');
        const result = tf.zeros(x.shape, resultDtype);
        const vals = this.readSync(result.dataId);
        const aVals = this.readSync(x.dataId);
        const finalDim = x.shape[x.rank - 1];
        const indexAdjuster = reverse ?
            (i, j) => i + finalDim - j - 1 :
            (i, j) => i + j;
        for (let i = 0; i < aVals.length; i += finalDim) {
            for (let j = 0; j < finalDim; j++) {
                const idx = indexAdjuster(i, j);
                if (j === 0) {
                    vals[idx] = exclusive ? 0 : aVals[idx];
                }
                else {
                    const prevIdx = indexAdjuster(i, j - 1);
                    vals[idx] = exclusive ? aVals[prevIdx] + vals[prevIdx] :
                        aVals[idx] + vals[prevIdx];
                }
            }
        }
        return result;
    }
    equal(a, b) {
        assertNotComplex([a, b], 'equal');
        return this.broadcastedBinaryOp(a, b, 'bool', (aVal, bVal) => {
            return (aVal === bVal) ? 1 : 0;
        });
    }
    notEqual(a, b) {
        assertNotComplex([a, b], 'notEqual');
        return this.broadcastedBinaryOp(a, b, 'bool', (aVal, bVal) => {
            return (aVal !== bVal) ? 1 : 0;
        });
    }
    less(a, b) {
        assertNotComplex([a, b], 'less');
        return this.broadcastedBinaryOp(a, b, 'bool', (aVal, bVal) => {
            return (aVal < bVal) ? 1 : 0;
        });
    }
    lessEqual(a, b) {
        assertNotComplex([a, b], 'lessEqual');
        return this.broadcastedBinaryOp(a, b, 'bool', (aVal, bVal) => {
            return (aVal <= bVal) ? 1 : 0;
        });
    }
    greater(a, b) {
        assertNotComplex([a, b], 'greater');
        return this.broadcastedBinaryOp(a, b, 'bool', (aVal, bVal) => {
            return (aVal > bVal) ? 1 : 0;
        });
    }
    greaterEqual(a, b) {
        assertNotComplex([a, b], 'greaterEqual');
        return this.broadcastedBinaryOp(a, b, 'bool', (aVal, bVal) => {
            return (aVal >= bVal) ? 1 : 0;
        });
    }
    logicalAnd(a, b) {
        assertNotComplex([a, b], 'logicalAnd');
        return this.broadcastedBinaryOp(a, b, 'bool', (aVal, bVal) => {
            return aVal && bVal;
        });
    }
    logicalOr(a, b) {
        assertNotComplex([a, b], 'logicalOr');
        return this.broadcastedBinaryOp(a, b, 'bool', (aVal, bVal) => {
            return aVal || bVal;
        });
    }
    select(condition, a, b) {
        assertNotComplex([condition, a, b], 'select');
        const values = this.readSync(condition.dataId);
        const aValues = this.readSync(a.dataId);
        const bValues = this.readSync(b.dataId);
        const result = tf.zeros(a.shape, upcastType(a.dtype, b.dtype));
        const newValues = this.readSync(result.dataId);
        let index = 0;
        const offset = condition.rank === 0 || condition.rank > 1 || a.rank === 1 ?
            1 :
            util.sizeFromShape(a.shape.slice(1));
        for (let i = 0; i < values.length; i++) {
            for (let j = 0; j < offset; j++) {
                if (values[i] === 1) {
                    newValues[index++] = aValues[i];
                }
                else {
                    newValues[index++] = bValues[i];
                }
            }
        }
        return result;
    }
    where(condition) {
        assertNotComplex([condition], 'where');
        const condVals = this.readSync(condition.dataId);
        return whereImpl(condition.shape, condVals);
    }
    topk(x, k, sorted) {
        assertNotComplex(x, 'topk');
        const xVals = this.readSync(x.dataId);
        return topkImpl(xVals, x.shape, x.dtype, k, sorted);
    }
    min(x, axes) {
        assertNotComplex(x, 'min');
        backend_util.assertAxesAreInnerMostDims('min', axes, x.rank);
        const [outShape, reduceShape] = backend_util.computeOutAndReduceShapes(x.shape, axes);
        const result = tf.zeros(outShape, x.dtype);
        const reduceSize = util.sizeFromShape(reduceShape);
        const vals = this.readSync(result.dataId);
        const aVals = this.readSync(x.dataId);
        for (let i = 0; i < vals.length; ++i) {
            const offset = i * reduceSize;
            let min = aVals[offset];
            for (let j = 0; j < reduceSize; ++j) {
                const value = aVals[offset + j];
                if (value < min) {
                    min = value;
                }
            }
            vals[i] = min;
        }
        return result;
    }
    minimum(a, b) {
        assertNotComplex([a, b], 'minimum');
        return this.broadcastedBinaryOp(a, b, a.dtype, (aVal, bVal) => Math.min(aVal, bVal));
    }
    mod(a, b) {
        assertNotComplex([a, b], 'mod');
        return this.broadcastedBinaryOp(a, b, a.dtype, (aVal, bVal) => {
            const rem = aVal % bVal;
            if ((aVal < 0 && bVal < 0) || (aVal >= 0 && bVal >= 0)) {
                return rem;
            }
            else {
                return (rem + bVal) % bVal;
            }
        });
    }
    maximum(a, b) {
        assertNotComplex([a, b], 'maximum');
        return this.broadcastedBinaryOp(a, b, a.dtype, (aVal, bVal) => Math.max(aVal, bVal));
    }
    all(x, axes) {
        assertNotComplex(x, 'all');
        backend_util.assertAxesAreInnerMostDims('all', axes, x.rank);
        const [outShape, reduceShape] = backend_util.computeOutAndReduceShapes(x.shape, axes);
        const result = tf.zeros(outShape, x.dtype);
        const reduceSize = util.sizeFromShape(reduceShape);
        const vals = this.readSync(result.dataId);
        const aVals = this.readSync(x.dataId);
        for (let i = 0; i < vals.length; ++i) {
            const offset = i * reduceSize;
            let all = aVals[offset];
            for (let j = 0; j < reduceSize; ++j) {
                const value = aVals[offset + j];
                all = all && value;
            }
            vals[i] = all;
        }
        return result;
    }
    any(x, axes) {
        assertNotComplex(x, 'any');
        backend_util.assertAxesAreInnerMostDims('any', axes, x.rank);
        const [outShape, reduceShape] = backend_util.computeOutAndReduceShapes(x.shape, axes);
        const result = tf.zeros(outShape, x.dtype);
        const reduceSize = util.sizeFromShape(reduceShape);
        const vals = this.readSync(result.dataId);
        const aVals = this.readSync(x.dataId);
        for (let i = 0; i < vals.length; ++i) {
            const offset = i * reduceSize;
            let anyVal = aVals[offset];
            for (let j = 0; j < reduceSize; ++j) {
                const value = aVals[offset + j];
                anyVal = anyVal || value;
            }
            vals[i] = anyVal;
        }
        return result;
    }
    squaredDifference(a, b) {
        assertNotComplex([a, b], 'squaredDifference');
        return this.broadcastedBinaryOp(a, b, a.dtype, (aVal, bVal) => {
            const diff = aVal - bVal;
            return diff * diff;
        });
    }
    eluDer(dy, y) {
        assertNotComplex([dy, y], 'eluDer');
        const resultValues = new Float32Array(y.size);
        const values = this.readSync(y.dataId);
        const dyValues = this.readSync(dy.dataId);
        for (let i = 0; i < values.length; ++i) {
            const v = values[i];
            if (v >= 1) {
                resultValues[i] = dyValues[i];
            }
            else {
                resultValues[i] = dyValues[i] * (v + 1);
            }
        }
        return this.makeOutput(resultValues, y.shape, 'float32');
    }
    atan2(a, b) {
        assertNotComplex([a, b], 'atan2');
        return this.broadcastedBinaryOp(a, b, a.dtype, (aValue, bValue) => Math.atan2(aValue, bValue));
    }
    tile(x, reps) {
        assertNotComplex(x, 'tile');
        return tile(this.bufferSync(x), reps);
    }
    gather(x, indices, axis) {
        assertNotComplex([x, indices], 'gather');
        const newShape = x.shape.slice();
        const indicesValues = this.readSync(indices.dataId);
        newShape[axis] = indicesValues.length;
        const result = tf.buffer(newShape, x.dtype);
        const xBuf = this.bufferSync(x);
        for (let i = 0; i < result.size; ++i) {
            const newLoc = result.indexToLoc(i);
            const originalLoc = newLoc.slice();
            originalLoc[axis] = indicesValues[newLoc[axis]];
            const originalIndex = xBuf.locToIndex(originalLoc);
            result.values[i] = xBuf.values[originalIndex];
        }
        return result.toTensor();
    }
    batchToSpaceND(x, blockShape, crops) {
        assertNotComplex([x], 'batchToSpaceND');
        const prod = blockShape.reduce((a, b) => a * b);
        const reshaped = backend_util.getReshaped(x.shape, blockShape, prod);
        const permuted = backend_util.getPermuted(reshaped.length, blockShape.length);
        const reshapedPermuted = backend_util.getReshapedPermuted(x.shape, blockShape, prod);
        const sliceBeginCoords = backend_util.getSliceBeginCoords(crops, blockShape.length);
        const sliceSize = backend_util.getSliceSize(reshapedPermuted, crops, blockShape.length);
        return tf.transpose(x.reshape(reshaped), permuted)
            .reshape(reshapedPermuted)
            .slice(sliceBeginCoords, sliceSize);
    }
    pool3d(x, convInfo, poolType) {
        assertNotComplex(x, 'pool3d');
        const strideDepth = convInfo.strideDepth;
        const strideHeight = convInfo.strideHeight;
        const strideWidth = convInfo.strideWidth;
        const dilationDepth = convInfo.dilationDepth;
        const dilationHeight = convInfo.dilationHeight;
        const dilationWidth = convInfo.dilationWidth;
        const effectiveFilterDepth = convInfo.effectiveFilterDepth;
        const effectiveFilterHeight = convInfo.effectiveFilterHeight;
        const effectiveFilterWidth = convInfo.effectiveFilterWidth;
        const padFront = convInfo.padInfo.front;
        const padTop = convInfo.padInfo.top;
        const padLeft = convInfo.padInfo.left;
        const initialValue = (poolType === 'max' ? Number.NEGATIVE_INFINITY :
            Number.POSITIVE_INFINITY);
        const xValues = this.readSync(x.dataId);
        const output = tf.buffer(convInfo.outShape, x.dtype);
        const outputVals = output.values;
        const outputBatchStrides = convInfo.outShape[1] * convInfo.outShape[2] *
            convInfo.outShape[3] * convInfo.outShape[4];
        const outputDepthStrides = convInfo.outShape[2] * convInfo.outShape[3] * convInfo.outShape[4];
        const outputRowStrides = convInfo.outShape[3] * convInfo.outShape[4];
        const outputColStrides = convInfo.outShape[4];
        for (let batch = 0; batch < convInfo.batchSize; ++batch) {
            const outputBatchOffset = batch * outputBatchStrides;
            const inputBatchOffset = batch * x.strides[0];
            for (let channel = 0; channel < convInfo.inChannels; ++channel) {
                for (let yDepth = 0; yDepth < convInfo.outDepth; ++yDepth) {
                    const xDepthCorner = yDepth * strideDepth - padFront;
                    let xDepthMin = xDepthCorner;
                    while (xDepthMin < 0) {
                        xDepthMin += dilationDepth;
                    }
                    const xDepthMax = Math.min(convInfo.inDepth, effectiveFilterDepth + xDepthCorner);
                    const outputDepthOffset = outputBatchOffset + yDepth * outputDepthStrides;
                    for (let yRow = 0; yRow < convInfo.outHeight; ++yRow) {
                        const xRowCorner = yRow * strideHeight - padTop;
                        let xRowMin = xRowCorner;
                        while (xRowMin < 0) {
                            xRowMin += dilationHeight;
                        }
                        const xRowMax = Math.min(convInfo.inHeight, effectiveFilterHeight + xRowCorner);
                        const outputRowOffset = outputDepthOffset + yRow * outputRowStrides;
                        for (let yCol = 0; yCol < convInfo.outWidth; ++yCol) {
                            const xColCorner = yCol * strideWidth - padLeft;
                            let xColMin = xColCorner;
                            while (xColMin < 0) {
                                xColMin += dilationWidth;
                            }
                            const xColMax = Math.min(convInfo.inWidth, effectiveFilterWidth + xColCorner);
                            // Shader code begins
                            const outputColOffset = outputRowOffset + yCol * outputColStrides;
                            let minMaxValue = initialValue;
                            let avgValue = 0;
                            let count = 0;
                            for (let xDepth = xDepthMin; xDepth < xDepthMax; xDepth += dilationDepth) {
                                const xDepthOffset = inputBatchOffset + xDepth * x.strides[1];
                                for (let xRow = xRowMin; xRow < xRowMax; xRow += dilationHeight) {
                                    const xRowOffset = xDepthOffset + xRow * x.strides[2];
                                    for (let xCol = xColMin; xCol < xColMax; xCol += dilationWidth) {
                                        const xColOffset = xRowOffset + xCol * x.strides[3];
                                        const pixel = xValues[xColOffset + channel];
                                        if ((poolType === 'max' && pixel > minMaxValue)) {
                                            minMaxValue = pixel;
                                        }
                                        else if (poolType === 'avg') {
                                            avgValue += pixel;
                                            count++;
                                        }
                                        if (isNaN(minMaxValue)) {
                                            break;
                                        }
                                    }
                                    if (isNaN(minMaxValue)) {
                                        break;
                                    }
                                }
                                if (isNaN(minMaxValue)) {
                                    break;
                                }
                            }
                            const outputOffset = outputColOffset + channel;
                            outputVals[outputOffset] =
                                poolType === 'avg' ? avgValue / count : minMaxValue;
                        }
                    }
                }
            }
        }
        return output.toTensor();
    }
    avgPool3d(x, convInfo) {
        assertNotComplex(x, 'avgPool3d');
        return this.pool3d(x, convInfo, 'avg').toFloat();
    }
    avgPool3dBackprop(dy, x, convInfo) {
        assertNotComplex([dy, x], 'avgPool3dBackprop');
        const strideDepth = convInfo.strideDepth;
        const strideHeight = convInfo.strideHeight;
        const strideWidth = convInfo.strideWidth;
        const filterDepth = convInfo.filterDepth;
        const filterHeight = convInfo.filterHeight;
        const filterWidth = convInfo.filterWidth;
        const dilationDepth = convInfo.dilationDepth;
        const dilationHeight = convInfo.dilationHeight;
        const dilationWidth = convInfo.dilationWidth;
        const effectiveFilterDepth = convInfo.effectiveFilterDepth;
        const effectiveFilterHeight = convInfo.effectiveFilterHeight;
        const effectiveFilterWidth = convInfo.effectiveFilterWidth;
        const padFront = effectiveFilterDepth - 1 - convInfo.padInfo.front;
        const padLeft = effectiveFilterWidth - 1 - convInfo.padInfo.left;
        const padTop = effectiveFilterHeight - 1 - convInfo.padInfo.top;
        const dx = tf.buffer(x.shape, 'float32');
        const avgMultiplier = 1 / (filterDepth * filterHeight * filterWidth);
        const dyBuf = this.bufferSync(dy);
        for (let batch = 0; batch < convInfo.batchSize; ++batch) {
            for (let channel = 0; channel < convInfo.inChannels; ++channel) {
                for (let dxDepth = 0; dxDepth < convInfo.inDepth; ++dxDepth) {
                    for (let dxRow = 0; dxRow < convInfo.inHeight; ++dxRow) {
                        for (let dxCol = 0; dxCol < convInfo.inWidth; ++dxCol) {
                            // Shader code begins.
                            const dyDepthCorner = dxDepth - padFront;
                            const dyRowCorner = dxRow - padTop;
                            const dyColCorner = dxCol - padLeft;
                            let dotProd = 0;
                            for (let wDepth = 0; wDepth < effectiveFilterDepth; wDepth += dilationDepth) {
                                const dyDepth = (dyDepthCorner + wDepth) / strideDepth;
                                if (dyDepth < 0 || dyDepth >= convInfo.outDepth ||
                                    Math.floor(dyDepth) !== dyDepth) {
                                    continue;
                                }
                                for (let wRow = 0; wRow < effectiveFilterHeight; wRow += dilationHeight) {
                                    const dyRow = (dyRowCorner + wRow) / strideHeight;
                                    if (dyRow < 0 || dyRow >= convInfo.outHeight ||
                                        Math.floor(dyRow) !== dyRow) {
                                        continue;
                                    }
                                    for (let wCol = 0; wCol < effectiveFilterWidth; wCol += dilationWidth) {
                                        const dyCol = (dyColCorner + wCol) / strideWidth;
                                        if (dyCol < 0 || dyCol >= convInfo.outWidth ||
                                            Math.floor(dyCol) !== dyCol) {
                                            continue;
                                        }
                                        const pixel = dyBuf.get(batch, dyDepth, dyRow, dyCol, channel);
                                        dotProd += pixel;
                                    }
                                }
                            }
                            dx.set(dotProd * avgMultiplier, batch, dxDepth, dxRow, dxCol, channel);
                        }
                    }
                }
            }
        }
        return dx.toTensor();
    }
    maxPool3d(x, convInfo) {
        assertNotComplex(x, 'maxPool3d');
        return this.pool3d(x, convInfo, 'max').toFloat();
    }
    maxPool3dPositions(x, convInfo) {
        const maxPositions = tf.buffer(convInfo.outShape, 'int32');
        const strideDepth = convInfo.strideDepth;
        const strideHeight = convInfo.strideHeight;
        const strideWidth = convInfo.strideWidth;
        const dilationDepth = convInfo.dilationDepth;
        const dilationHeight = convInfo.dilationHeight;
        const dilationWidth = convInfo.dilationWidth;
        const effectiveFilterDepth = convInfo.effectiveFilterDepth;
        const effectiveFilterHeight = convInfo.effectiveFilterHeight;
        const effectiveFilterWidth = convInfo.effectiveFilterWidth;
        const padFront = convInfo.padInfo.front;
        const padTop = convInfo.padInfo.top;
        const padLeft = convInfo.padInfo.left;
        const xBuf = this.bufferSync(x);
        for (let batch = 0; batch < convInfo.batchSize; ++batch) {
            for (let channel = 0; channel < convInfo.inChannels; ++channel) {
                for (let yDepth = 0; yDepth < convInfo.outDepth; ++yDepth) {
                    const xDepthCorner = yDepth * strideDepth - padFront;
                    let xDepthMin = xDepthCorner;
                    while (xDepthMin < 0) {
                        xDepthMin += dilationDepth;
                    }
                    const xDepthMax = Math.min(convInfo.inDepth, effectiveFilterDepth + xDepthCorner);
                    for (let yRow = 0; yRow < convInfo.outHeight; ++yRow) {
                        const xRowCorner = yRow * strideHeight - padTop;
                        let xRowMin = xRowCorner;
                        while (xRowMin < 0) {
                            xRowMin += dilationHeight;
                        }
                        const xRowMax = Math.min(convInfo.inHeight, effectiveFilterHeight + xRowCorner);
                        for (let yCol = 0; yCol < convInfo.outWidth; ++yCol) {
                            const xColCorner = yCol * strideWidth - padLeft;
                            let xColMin = xColCorner;
                            while (xColMin < 0) {
                                xColMin += dilationWidth;
                            }
                            const xColMax = Math.min(convInfo.inWidth, effectiveFilterWidth + xColCorner);
                            // Shader code begins
                            let maxValue = Number.NEGATIVE_INFINITY;
                            let maxPosition = -1;
                            for (let xDepth = xDepthMin; xDepth < xDepthMax; xDepth += dilationDepth) {
                                const wDepth = xDepth - xDepthCorner;
                                for (let xRow = xRowMin; xRow < xRowMax; xRow += dilationHeight) {
                                    const wRow = xRow - xRowCorner;
                                    for (let xCol = xColMin; xCol < xColMax; xCol += dilationWidth) {
                                        const wCol = xCol - xColCorner;
                                        const pixel = xBuf.get(batch, xDepth, xRow, xCol, channel);
                                        if (pixel >= maxValue) {
                                            maxValue = pixel;
                                            maxPosition = wDepth * effectiveFilterHeight *
                                                effectiveFilterWidth +
                                                wRow * effectiveFilterHeight + wCol;
                                        }
                                    }
                                }
                            }
                            maxPositions.set(maxPosition, batch, yDepth, yRow, yCol, channel);
                        }
                    }
                }
            }
        }
        return maxPositions.toTensor();
    }
    maxPool3dBackprop(dy, x, y, convInfo) {
        assertNotComplex([x, y], 'maxPool3dBackprop');
        const maxPositions = this.maxPool3dPositions(x, convInfo);
        const strideDepth = convInfo.strideDepth;
        const strideHeight = convInfo.strideHeight;
        const strideWidth = convInfo.strideWidth;
        const dilationDepth = convInfo.dilationDepth;
        const dilationHeight = convInfo.dilationHeight;
        const dilationWidth = convInfo.dilationWidth;
        const effectiveFilterDepth = convInfo.effectiveFilterDepth;
        const effectiveFilterHeight = convInfo.effectiveFilterHeight;
        const effectiveFilterWidth = convInfo.effectiveFilterWidth;
        const padFront = effectiveFilterDepth - 1 - convInfo.padInfo.front;
        const padLeft = effectiveFilterWidth - 1 - convInfo.padInfo.left;
        const padTop = effectiveFilterHeight - 1 - convInfo.padInfo.top;
        const dx = tf.buffer(x.shape, 'float32');
        const maxPosBuf = this.bufferSync(maxPositions);
        const dyBuf = this.bufferSync(dy);
        for (let batch = 0; batch < convInfo.batchSize; ++batch) {
            for (let channel = 0; channel < convInfo.inChannels; ++channel) {
                for (let dxDepth = 0; dxDepth < convInfo.inDepth; ++dxDepth) {
                    for (let dxRow = 0; dxRow < convInfo.inHeight; ++dxRow) {
                        for (let dxCol = 0; dxCol < convInfo.inWidth; ++dxCol) {
                            // Shader code begins
                            const dyDepthCorner = dxDepth - padFront;
                            const dyRowCorner = dxRow - padTop;
                            const dyColCorner = dxCol - padLeft;
                            let dotProd = 0;
                            for (let wDepth = 0; wDepth < effectiveFilterDepth; wDepth += dilationDepth) {
                                const dyDepth = (dyDepthCorner + wDepth) / strideDepth;
                                if (dyDepth < 0 || dyDepth >= convInfo.outDepth ||
                                    Math.floor(dyDepth) !== dyDepth) {
                                    continue;
                                }
                                for (let wRow = 0; wRow < effectiveFilterHeight; wRow += dilationHeight) {
                                    const dyRow = (dyRowCorner + wRow) / strideHeight;
                                    if (dyRow < 0 || dyRow >= convInfo.outHeight ||
                                        Math.floor(dyRow) !== dyRow) {
                                        continue;
                                    }
                                    for (let wCol = 0; wCol < effectiveFilterWidth; wCol += dilationWidth) {
                                        const dyCol = (dyColCorner + wCol) / strideWidth;
                                        if (dyCol < 0 || dyCol >= convInfo.outWidth ||
                                            Math.floor(dyCol) !== dyCol) {
                                            continue;
                                        }
                                        const maxPos = effectiveFilterDepth *
                                            effectiveFilterHeight * effectiveFilterWidth -
                                            1 -
                                            maxPosBuf.get(batch, dyDepth, dyRow, dyCol, channel);
                                        const curPos = wDepth * effectiveFilterHeight * effectiveFilterWidth +
                                            wRow * effectiveFilterWidth + wCol;
                                        const mask = maxPos === curPos ? 1 : 0;
                                        if (mask === 0) {
                                            continue;
                                        }
                                        const pixel = dyBuf.get(batch, dyDepth, dyRow, dyCol, channel);
                                        dotProd += pixel * mask;
                                    }
                                }
                            }
                            dx.set(dotProd, batch, dxDepth, dxRow, dxCol, channel);
                        }
                    }
                }
            }
        }
        return dx.toTensor();
    }
    resizeBilinear(x, newHeight, newWidth, alignCorners) {
        assertNotComplex(x, 'resizeBilinear');
        const [batch, oldHeight, oldWidth, numChannels] = x.shape;
        const xValues = this.readSync(x.dataId);
        const result = new Float32Array(util.sizeFromShape([batch, newHeight, newWidth, numChannels]));
        const effectiveInputSize = [
            (alignCorners && newHeight > 1) ? oldHeight - 1 : oldHeight,
            (alignCorners && newWidth > 1) ? oldWidth - 1 : oldWidth
        ];
        const effectiveOutputSize = [
            (alignCorners && newHeight > 1) ? newHeight - 1 : newHeight,
            (alignCorners && newWidth > 1) ? newWidth - 1 : newWidth
        ];
        let outputIdx = 0;
        const effectiveRowSizeRatio = effectiveInputSize[0] / effectiveOutputSize[0];
        const effectiveColSizeRatio = effectiveInputSize[1] / effectiveOutputSize[1];
        for (let b = 0; b < batch; b++) {
            for (let r = 0; r < newHeight; r++) {
                const sourceFracRow = effectiveRowSizeRatio * r;
                const sourceRowFloor = Math.floor(sourceFracRow);
                const rowFrac = sourceFracRow - sourceRowFloor;
                const sourceRowCeil = Math.min(oldHeight - 1, Math.ceil(sourceFracRow));
                const topRowOffset = b * x.strides[0] + sourceRowFloor * x.strides[1];
                const botRowOffset = b * x.strides[0] + sourceRowCeil * x.strides[1];
                for (let c = 0; c < newWidth; c++) {
                    const sourceFracCol = effectiveColSizeRatio * c;
                    const sourceColFloor = Math.floor(sourceFracCol);
                    const colFrac = sourceFracCol - sourceColFloor;
                    const sourceColCeil = Math.min(oldWidth - 1, Math.ceil(sourceFracCol));
                    const topLeftOffest = topRowOffset + sourceColFloor * x.strides[2];
                    const botLeftOffset = botRowOffset + sourceColFloor * x.strides[2];
                    const topRightOffset = topRowOffset + sourceColCeil * x.strides[2];
                    const botRightOffest = botRowOffset + sourceColCeil * x.strides[2];
                    for (let d = 0; d < numChannels; d++) {
                        // Begin shader.
                        // Compute the fractional index of the source.
                        const topLeft = xValues[topLeftOffest + d];
                        const bottomLeft = xValues[botLeftOffset + d];
                        const topRight = xValues[topRightOffset + d];
                        const bottomRight = xValues[botRightOffest + d];
                        const top = topLeft + (topRight - topLeft) * colFrac;
                        const bottom = bottomLeft + (bottomRight - bottomLeft) * colFrac;
                        const newValue = top + (bottom - top) * rowFrac;
                        result[outputIdx++] = newValue;
                    }
                }
            }
        }
        return tf.tensor(result, [batch, newHeight, newWidth, numChannels]);
    }
    resizeBilinearBackprop(dy, x, alignCorners) {
        assertNotComplex([dy, x], 'resizeBilinearBackprop');
        const [batch, xHeight, xWidth, depth] = x.shape;
        const [, yHeight, yWidth] = dy.shape;
        const output = new Float32Array(batch * xHeight * xWidth * depth);
        // In the backwards pass, we want to find the pixels that were generated
        // for each pixel in the input image the forward pass and add the
        // corresponding coefficient from dy to the gradient (with some
        // interpolation).
        const effectiveXSize = [
            (alignCorners && yHeight > 1) ? xHeight - 1 : xHeight,
            (alignCorners && yWidth > 1) ? xWidth - 1 : xWidth
        ];
        const effectiveYSize = [
            (alignCorners && yHeight > 1) ? yHeight - 1 : yHeight,
            (alignCorners && yWidth > 1) ? yWidth - 1 : yWidth
        ];
        const heightScale = effectiveXSize[0] / effectiveYSize[0];
        const widthScale = effectiveXSize[1] / effectiveYSize[1];
        // Reference implementation
        // tslint:disable-next-line:max-line-length
        // https://github.com/tensorflow/tensorflow/blob/3039375c86a5bbc9610c7725dcaa95d635f87ba2/tensorflow/core/kernels/resize_bilinear_op.cc#L275
        const dyValues = this.readSync(dy.dataId);
        let offset = 0;
        for (let b = 0; b < batch; b++) {
            const bOffset = b * x.strides[0];
            for (let r = 0; r < yHeight; r++) {
                const dxR = r * heightScale;
                const topDxRIndex = Math.floor(dxR);
                const bottomDxRIndex = Math.min(Math.ceil(dxR), xHeight - 1);
                const topDxROffset = bOffset + topDxRIndex * x.strides[1];
                const bottomDxROffset = bOffset + bottomDxRIndex * x.strides[1];
                const dxRLerp = dxR - topDxRIndex;
                const inverseDxRLerp = 1.0 - dxRLerp;
                for (let c = 0; c < yWidth; c++) {
                    const dxC = c * widthScale;
                    const leftDxCIndex = Math.floor(dxC);
                    const rightDxCIndex = Math.min(Math.ceil(dxC), xWidth - 1);
                    const dxCLerp = dxC - leftDxCIndex;
                    const inverseDxCLerp = 1.0 - dxCLerp;
                    const topLeftRCOffset = topDxROffset + leftDxCIndex * x.strides[2];
                    const topRightRCOffset = topDxROffset + rightDxCIndex * x.strides[2];
                    const bottomLeftRCOffset = bottomDxROffset + leftDxCIndex * x.strides[2];
                    const bottomRightRCOffset = bottomDxROffset + rightDxCIndex * x.strides[2];
                    const inverseDxRLerpTimesInverseDxCLerp = inverseDxRLerp * inverseDxCLerp;
                    const inverseDxRLerpTimesDxCLerp = inverseDxRLerp * dxCLerp;
                    const dxRLerpTimesInverseDxCLerp = dxRLerp * inverseDxCLerp;
                    const dxRLerpTimesDxCLerp = dxRLerp * dxCLerp;
                    for (let d = 0; d < depth; d++) {
                        const dyVal = dyValues[offset++];
                        output[topLeftRCOffset + d] +=
                            dyVal * inverseDxRLerpTimesInverseDxCLerp;
                        output[topRightRCOffset + d] += dyVal * inverseDxRLerpTimesDxCLerp;
                        output[bottomLeftRCOffset + d] +=
                            dyVal * dxRLerpTimesInverseDxCLerp;
                        output[bottomRightRCOffset + d] += dyVal * dxRLerpTimesDxCLerp;
                    }
                }
            }
        }
        return tf.tensor4d(output, [batch, xWidth, xHeight, depth], x.dtype);
    }
    resizeNearestNeighbor(x, newHeight, newWidth, alignCorners) {
        assertNotComplex(x, 'resizeNearestNeighbor');
        const [batch, oldHeight, oldWidth, numChannels] = x.shape;
        const xValues = this.readSync(x.dataId);
        const output = new Float32Array(batch * newHeight * newWidth * numChannels);
        const effectiveInputSize = [
            (alignCorners && newHeight > 1) ? oldHeight - 1 : oldHeight,
            (alignCorners && newWidth > 1) ? oldWidth - 1 : oldWidth
        ];
        const effectiveOutputSize = [
            (alignCorners && newHeight > 1) ? newHeight - 1 : newHeight,
            (alignCorners && newWidth > 1) ? newWidth - 1 : newWidth
        ];
        const effectiveRowSizeRatio = effectiveInputSize[0] / effectiveOutputSize[0];
        const effectiveColSizeRatio = effectiveInputSize[1] / effectiveOutputSize[1];
        let outputOffset = 0;
        for (let b = 0; b < batch; b++) {
            const batchOffset = b * x.strides[0];
            for (let r = 0; r < newHeight; r++) {
                const sourceFracRow = effectiveRowSizeRatio * r;
                const sourceNearestRow = Math.min(oldHeight - 1, alignCorners ? Math.round(sourceFracRow) :
                    Math.floor(sourceFracRow));
                const rowOffset = batchOffset + sourceNearestRow * x.strides[1];
                for (let c = 0; c < newWidth; c++) {
                    const sourceFracCol = effectiveColSizeRatio * c;
                    const sourceNearestCol = Math.min(oldWidth - 1, alignCorners ? Math.round(sourceFracCol) :
                        Math.floor(sourceFracCol));
                    const colOffset = rowOffset + sourceNearestCol * x.strides[2];
                    for (let d = 0; d < numChannels; d++) {
                        // Begin shader.
                        // Compute the fractional index of the source.
                        const newVal = xValues[colOffset + d];
                        output[outputOffset++] = newVal;
                    }
                }
            }
        }
        return tf.tensor(output, [batch, newHeight, newWidth, numChannels], x.dtype);
    }
    resizeNearestNeighborBackprop(dy, x, alignCorners) {
        assertNotComplex([dy, x], 'resizeNearestNeighborBackprop');
        const [batch, xHeight, xWidth, depth] = x.shape;
        const [, yHeight, yWidth] = dy.shape;
        const output = new Float32Array(batch * xHeight * xWidth * depth);
        const dyValues = this.readSync(dy.dataId);
        // In the backwards pass, we want to find the pixels that were generated
        // for each pixel in the input image the forward pass
        const effectiveXSize = [
            (alignCorners && yHeight > 1) ? xHeight - 1 : xHeight,
            (alignCorners && yWidth > 1) ? xWidth - 1 : xWidth
        ];
        const effectiveYSize = [
            (alignCorners && yHeight > 1) ? yHeight - 1 : yHeight,
            (alignCorners && yWidth > 1) ? yWidth - 1 : yWidth
        ];
        const heightScale = effectiveXSize[0] / effectiveYSize[0];
        const widthScale = effectiveXSize[1] / effectiveYSize[1];
        const invHeightScale = 1 / heightScale;
        const invWidthScale = 1 / widthScale;
        // This defines the size of the window of values around a particular
        // index in dy that we want to search for contributions to dx.
        const winHeight = (Math.ceil(invHeightScale) * 2) + 2;
        const winWidth = (Math.ceil(invWidthScale) * 2) + 2;
        // Loop over the output space.
        for (let b = 0; b < batch; b++) {
            const batchOffset = b * x.strides[0];
            for (let r = 0; r < xHeight; r++) {
                const rowOffset = batchOffset + r * x.strides[1];
                // Compute bounds for where in dy we will look
                const startRLerp = Math.floor(r * invHeightScale);
                const startDyR = Math.floor(startRLerp - (winHeight / 2));
                for (let c = 0; c < xWidth; c++) {
                    const colOffset = rowOffset + c * x.strides[2];
                    // Compute bounds for where in dy we will look
                    const startCLerp = Math.floor(c * invWidthScale);
                    const startDyC = Math.floor(startCLerp - (winWidth / 2));
                    for (let d = 0; d < depth; d++) {
                        let accum = 0;
                        // loop over dy
                        for (let dyRIndex = 0; dyRIndex < winHeight; dyRIndex++) {
                            const dyR = dyRIndex + startDyR;
                            // Guard against the window exceeding the bounds of dy
                            if (dyR < 0 || dyR >= yHeight) {
                                continue;
                            }
                            const dyROffset = batchOffset + dyR * dy.strides[1];
                            const sourceFracRow = dyR * heightScale;
                            const sourceNearestRow = Math.min(xHeight - 1, alignCorners ? Math.round(sourceFracRow) :
                                Math.floor(sourceFracRow));
                            if (r !== sourceNearestRow) {
                                continue;
                            }
                            for (let dyCIndex = 0; dyCIndex < winWidth; dyCIndex++) {
                                const dyC = dyCIndex + startDyC;
                                // Guard against the window exceeding the bounds of dy
                                if (dyC < 0 || dyC >= yWidth) {
                                    continue;
                                }
                                const dyCOffset = dyROffset + dyC * dy.strides[2];
                                const sourceFracCol = dyC * widthScale;
                                const sourceNearestCol = Math.min(xWidth - 1, alignCorners ? Math.round(sourceFracCol) :
                                    Math.floor(sourceFracCol));
                                if (c === sourceNearestCol) {
                                    accum += dyValues[dyCOffset + d];
                                }
                            }
                        }
                        output[colOffset + d] = accum;
                    }
                }
            }
        }
        return tf.tensor4d(output, x.shape, x.dtype);
    }
    localResponseNormalization4D(x, depthRadius, bias, alpha, beta) {
        assertNotComplex(x, 'localResponseNormalization4D');
        const channels = x.shape[3];
        const maxD = channels - 1;
        const xValues = this.readSync(x.dataId);
        const size = x.size;
        const result = new Float32Array(size);
        function sumAcrossChannels(offset) {
            const currentChannel = offset % channels;
            let beginSumOffset = offset - currentChannel + Math.max(0, currentChannel - depthRadius);
            const endSumOffset = offset - currentChannel +
                Math.min(currentChannel + depthRadius, maxD);
            let sum = 0.0;
            for (; beginSumOffset <= endSumOffset; beginSumOffset++) {
                const z = xValues[beginSumOffset];
                sum += z * z;
            }
            return sum;
        }
        for (let offset = 0; offset < size; offset++) {
            const sum = sumAcrossChannels(offset);
            const val = xValues[offset] * Math.pow(bias + alpha * sum, -beta);
            result[offset] = val;
        }
        return tf.tensor4d(result, x.shape);
    }
    LRNGrad(dy, inputImage, outputImage, depthRadius, bias, alpha, beta) {
        assertNotComplex(dy, 'LRNGrad');
        const channels = dy.shape[3];
        const dyValues = this.readSync(dy.dataId);
        const inputImageValues = this.readSync(inputImage.dataId);
        const outputImageValues = this.readSync(outputImage.dataId);
        const result = new Float32Array(dy.size);
        const size = dy.size;
        for (let offset = 0; offset < size; offset++) {
            const currentChannel = offset % channels;
            const depthBegin = (offset - currentChannel) + Math.max(0, currentChannel - depthRadius);
            const depthEnd = (offset - currentChannel) +
                Math.min(channels, currentChannel + depthRadius + 1);
            let norm = 0;
            for (let k = depthBegin; k < depthEnd; k++) {
                norm += Math.pow(inputImageValues[k], 2);
            }
            norm = alpha * norm + bias;
            for (let k = depthBegin; k < depthEnd; k++) {
                let dyi = -2 * alpha * beta * inputImageValues[k] *
                    outputImageValues[offset] / norm;
                if (offset === k) {
                    dyi += Math.pow(norm, -beta);
                }
                dyi *= dyValues[offset];
                result[k] += dyi;
            }
        }
        return tf.tensor4d(result, dy.shape);
    }
    multinomial(logits, normalized, numSamples, seed) {
        assertNotComplex(logits, 'multinomial');
        const probabilities = normalized ? logits : tf.softmax(logits);
        const batchSize = probabilities.shape[0];
        const numEvents = probabilities.shape[1];
        const res = tf.zeros([batchSize, numSamples], 'int32');
        const resVals = this.readSync(res.dataId);
        const probVals = this.readSync(probabilities.dataId);
        for (let b = 0; b < batchSize; ++b) {
            const offset = b * numEvents;
            // The cdf won't include the last event. It will be implicit if no other
            // event happened.
            const cdf = new Float32Array(numEvents - 1);
            cdf[0] = probVals[offset];
            for (let event = 1; event < cdf.length; ++event) {
                cdf[event] = cdf[event - 1] + probVals[offset + event];
            }
            const random = seedrandom.alea(seed.toString());
            const outOffset = b * numSamples;
            for (let sampleId = 0; sampleId < numSamples; ++sampleId) {
                const r = random();
                // Assume last event happened by default.
                resVals[outOffset + sampleId] = cdf.length;
                for (let event = 0; event < cdf.length; event++) {
                    if (r < cdf[event]) {
                        resVals[outOffset + sampleId] = event;
                        break;
                    }
                }
            }
        }
        return res;
    }
    oneHot(indices, depth, onValue, offValue) {
        assertNotComplex(indices, 'oneHot');
        const res = new Float32Array(indices.size * depth);
        res.fill(offValue);
        const indicesVal = this.readSync(indices.dataId);
        for (let event = 0; event < indices.size; ++event) {
            if (indicesVal[event] >= 0 && indicesVal[event] < depth) {
                res[event * depth + indicesVal[event]] = onValue;
            }
        }
        return tf.tensor2d(res, [indices.size, depth], 'int32');
    }
    nonMaxSuppression(boxes, scores, maxOutputSize, iouThreshold, scoreThreshold) {
        assertNotComplex(boxes, 'nonMaxSuppression');
        const boxesVals = this.readSync(boxes.dataId);
        const scoresVals = this.readSync(scores.dataId);
        return nonMaxSuppressionV3Impl(boxesVals, scoresVals, maxOutputSize, iouThreshold, scoreThreshold);
    }
    depthToSpace(x, blockSize, dataFormat) {
        util.assert(dataFormat === 'NHWC', () => `Only NHWC dataFormat supported on CPU for depthToSpace. Got ${dataFormat}`);
        util.assert(blockSize > 1, () => `blockSize should be > 1 for depthToSpace, but was: ${blockSize}`);
        const batchSize = x.shape[0];
        const inputHeight = x.shape[1];
        const inputWidth = x.shape[2];
        const inputDepth = x.shape[3];
        const outputHeight = inputHeight * blockSize;
        const outputWidth = inputWidth * blockSize;
        const outputDepth = inputDepth / (blockSize * blockSize);
        const xValues = this.readSync(x.dataId);
        const result = new Float32Array(batchSize * outputHeight * outputWidth * outputDepth);
        let outputIdx = 0;
        for (let b = 0; b < batchSize; ++b) {
            for (let h = 0; h < outputHeight; ++h) {
                const inH = Math.floor(h / blockSize);
                const offsetH = (h % blockSize);
                for (let w = 0; w < outputWidth; ++w) {
                    const inW = Math.floor(w / blockSize);
                    const offsetW = (w % blockSize);
                    const offsetD = (offsetH * blockSize + offsetW) * outputDepth;
                    for (let d = 0; d < outputDepth; ++d) {
                        const inD = d + offsetD;
                        const inputIdx = inD + inputDepth * (inW + inputWidth * (inH + inputHeight * b));
                        result[outputIdx++] = xValues[inputIdx];
                    }
                }
            }
        }
        return tf.tensor4d(result, [batchSize, outputHeight, outputWidth, outputDepth]);
    }
    broadcastedBinaryOp(a, b, dtype, op) {
        const newShape = backend_util.assertAndGetBroadcastShape(a.shape, b.shape);
        const result = tf.buffer(newShape, dtype);
        const aVals = this.readSync(a.dataId);
        const bVals = this.readSync(b.dataId);
        const aBroadcastDims = backend_util.getBroadcastDims(a.shape, newShape);
        const bBroadcastDims = backend_util.getBroadcastDims(b.shape, newShape);
        const resVals = result.values;
        if (aBroadcastDims.length + bBroadcastDims.length === 0) {
            for (let i = 0; i < resVals.length; ++i) {
                resVals[i] = op(aVals[i % aVals.length], bVals[i % bVals.length]);
            }
        }
        else {
            const aBuf = this.bufferSync(a);
            const bBuf = this.bufferSync(b);
            for (let i = 0; i < resVals.length; ++i) {
                const loc = result.indexToLoc(i);
                const aLoc = loc.slice(-a.rank);
                aBroadcastDims.forEach(d => aLoc[d] = 0);
                const aIndex = aBuf.locToIndex(aLoc);
                const bLoc = loc.slice(-b.rank);
                bBroadcastDims.forEach(d => bLoc[d] = 0);
                const bIndex = bBuf.locToIndex(bLoc);
                resVals[i] = op(aVals[aIndex], bVals[bIndex]);
            }
        }
        return result.toTensor();
    }
    split(x, sizeSplits, axis) {
        return split(x, sizeSplits, axis);
    }
    dispose() { }
    floatPrecision() {
        return 32;
    }
    /** Returns the smallest representable number.  */
    epsilon() {
        return super.epsilon();
    }
    cropAndResize(images, boxes, boxIndex, cropSize, method, extrapolationValue) {
        const [batch, imageHeight, imageWidth, numChannels] = images.shape;
        const numBoxes = boxes.shape[0];
        const [cropHeight, cropWidth] = cropSize;
        const output = tf.buffer([numBoxes, cropHeight, cropWidth, numChannels], 'float32');
        const boxVals = this.readSync(boxes.dataId);
        const boxIndVals = this.readSync(boxIndex.dataId);
        const imageVals = this.readSync(images.dataId);
        const inStride = images.strides; // to calculate flat indexes into image
        const outStride = output.strides; // to calculate flat indexes into output
        // Reference implementation
        // tslint:disable-next-line:max-line-length
        // https://github.com/tensorflow/tensorflow/blob/master/tensorflow/core/kernels/crop_and_resize_op.cc
        for (let b = 0; b < numBoxes; b++) {
            const startInd = b * 4;
            const y1 = boxVals[startInd];
            const x1 = boxVals[startInd + 1];
            const y2 = boxVals[startInd + 2];
            const x2 = boxVals[startInd + 3];
            const bInd = boxIndVals[b];
            if (bInd >= batch) {
                continue;
            }
            const heightScale = (cropHeight > 1) ?
                (y2 - y1) * (imageHeight - 1) / (cropHeight - 1) :
                0;
            const widthScale = (cropWidth > 1) ? (x2 - x1) * (imageWidth - 1) / (cropWidth - 1) : 0;
            for (let y = 0; y < cropHeight; y++) {
                const yInd = (cropHeight > 1) ?
                    y1 * (imageHeight - 1) + y * (heightScale) :
                    0.5 * (y1 + y2) * (imageHeight - 1);
                if (yInd < 0 || yInd > imageHeight - 1) {
                    for (let x = 0; x < cropWidth; x++) {
                        for (let c = 0; c < numChannels; c++) {
                            const ind = c + x * outStride[2] + y * outStride[1] + b * outStride[0];
                            output.values[ind] = extrapolationValue;
                        }
                    }
                    continue;
                }
                if (method === 'bilinear') {
                    const topInd = Math.floor(yInd);
                    const bottomInd = Math.ceil(yInd);
                    const yLerp = yInd - topInd;
                    for (let x = 0; x < cropWidth; x++) {
                        const xInd = (cropWidth > 1) ?
                            x1 * (imageWidth - 1) + x * widthScale :
                            0.5 * (x1 + x2) * (imageWidth - 1);
                        if (xInd < 0 || xInd > imageWidth - 1) {
                            for (let c = 0; c < numChannels; c++) {
                                const ind = c + x * outStride[2] + y * outStride[1] + b * outStride[0];
                                output.values[ind] = extrapolationValue;
                            }
                            continue;
                        }
                        const leftInd = Math.floor(xInd);
                        const rightInd = Math.ceil(xInd);
                        const xLerp = xInd - leftInd;
                        for (let c = 0; c < numChannels; c++) {
                            let ind = c + leftInd * inStride[2] + topInd * inStride[1] +
                                bInd * inStride[0];
                            const topLeft = imageVals[ind];
                            ind = c + rightInd * inStride[2] + topInd * inStride[1] +
                                bInd * inStride[0];
                            const topRight = imageVals[ind];
                            ind = c + leftInd * inStride[2] + bottomInd * inStride[1] +
                                bInd * inStride[0];
                            const bottomLeft = imageVals[ind];
                            ind = c + rightInd * inStride[2] + bottomInd * inStride[1] +
                                bInd * inStride[0];
                            const bottomRight = imageVals[ind];
                            const top = topLeft + (topRight - topLeft) * xLerp;
                            const bottom = bottomLeft + (bottomRight - bottomLeft) * xLerp;
                            ind = c + x * outStride[2] + y * outStride[1] + b * outStride[0];
                            output.values[ind] = top + ((bottom - top) * yLerp);
                        }
                    }
                }
                else { // method == "nearest"
                    for (let x = 0; x < cropWidth; ++x) {
                        const xInd = (cropWidth > 1) ?
                            x1 * (imageWidth - 1) + x * widthScale :
                            0.5 * (x1 + x2) * (imageWidth - 1);
                        if (xInd < 0 || xInd > imageWidth - 1) {
                            for (let c = 0; c < numChannels; c++) {
                                const ind = c + x * outStride[2] + y * outStride[1] + b * outStride[0];
                                output.values[ind] = extrapolationValue;
                            }
                            continue;
                        }
                        const closestX = Math.round(xInd);
                        const closestY = Math.round(yInd);
                        for (let c = 0; c < numChannels; c++) {
                            const inInd = c + closestX * inStride[2] +
                                closestY * inStride[1] + bInd * inStride[0];
                            const outInd = c + x * outStride[2] + y * outStride[1] + b * outStride[0];
                            output.values[outInd] = imageVals[inInd];
                        }
                    }
                }
            }
        }
        return output.toTensor();
    }
    sparseToDense(sparseIndices, sparseValues, outputShape, defaultValue) {
        const { sliceRank, numUpdates, sliceSize, strides, outputSize } = backend_util.calculateShapes(sparseValues, sparseIndices, outputShape);
        const sumDupeIndices = false;
        return this.scatter(sparseIndices, sparseValues, outputShape, outputSize, sliceSize, numUpdates, sliceRank, strides, defaultValue, sumDupeIndices);
    }
    gatherND(x, indices) {
        const indicesShape = indices.shape;
        const sliceRank = indicesShape[indicesShape.length - 1];
        const [resultShape, numSlices, sliceSize, strides] = backend_util.prepareAndValidate(x, indices);
        if (numSlices === 0) {
            return tf.tensor([], resultShape, x.dtype);
        }
        const buffer = new TensorBuffer([numSlices, sliceSize], x.dtype);
        const indicesData = this.readSync(indices.dataId);
        const xData = this.readSync(x.dataId);
        for (let i = 0; i < numSlices; i++) {
            const index = [];
            let flattenIndex = 0;
            for (let j = 0; j < sliceRank; j++) {
                const dim = indicesData[i * sliceRank + j];
                flattenIndex += dim * strides[j];
                index.push(dim);
            }
            if (flattenIndex < 0 || flattenIndex >= x.size / sliceSize) {
                throw new Error(`Invalid indices: ${index} does not index into ${x.shape}`);
            }
            for (let k = 0; k < sliceSize; k++) {
                buffer.values[i * sliceSize + k] = xData[flattenIndex * sliceSize + k];
            }
        }
        return buffer.toTensor().reshape(resultShape);
    }
    scatterND(indices, updates, shape) {
        const { sliceRank, numUpdates, sliceSize, strides, outputSize } = backend_util.calculateShapes(updates, indices, shape);
        const defaultValue = tf.scalar(0);
        const sumDupeIndices = true;
        return this.scatter(indices, updates, shape, outputSize, sliceSize, numUpdates, sliceRank, strides, defaultValue, sumDupeIndices);
    }
    onesLike(x) {
        if (x.dtype === 'string') {
            throw new Error('onesLike is not supported for string tensors');
        }
        else {
            // TODO(lina128): Use fill kernel directly once this kernel is
            // modularized.
            return tf.fill(x.shape, 1, x.dtype);
        }
    }
    zerosLike(x) {
        const values = util.getArrayFromDType(x.dtype, util.sizeFromShape(x.shape));
        return this.makeOutput(values, x.shape, x.dtype);
    }
    linspace(start, stop, num) {
        return backend_util.linspaceImpl(start, stop, num);
    }
    scatter(indices, updates, shape, outputSize, sliceSize, numUpdates, sliceRank, strides, defaultValue, sumDupeIndices) {
        const flattenShape = [outputSize / sliceSize, sliceSize];
        const indicesData = this.readSync(indices.dataId);
        const updatesData = this.readSync(updates.dataId);
        if (outputSize === 0) {
            return tf.tensor([], shape, updates.dtype);
        }
        const buffer = new TensorBuffer(flattenShape, updates.dtype);
        buffer.values.fill(this.readSync(defaultValue.dataId)[0]);
        for (let i = 0; i < numUpdates; i++) {
            const index = [];
            let flattenIndex = 0;
            for (let j = 0; j < sliceRank; j++) {
                const dim = indicesData[i * sliceRank + j];
                index.push(dim);
                flattenIndex += dim * strides[j];
            }
            if (flattenIndex < 0 || flattenIndex >= outputSize / sliceSize) {
                throw new Error(`Invalid indices: ${index} does not index into ${shape}`);
            }
            for (let k = 0; k < sliceSize; k++) {
                if (sumDupeIndices) {
                    buffer.values[flattenIndex * sliceSize + k] +=
                        updatesData[i * sliceSize + k];
                }
                else {
                    buffer.values[flattenIndex * sliceSize + k] = updates.rank === 0 ?
                        updatesData[0] :
                        updatesData[i * sliceSize + k];
                }
            }
        }
        return buffer.toTensor().reshape(shape);
    }
}
//# sourceMappingURL=backend_cpu.js.map